import { useEffect, useState, useRef } from "react";
// redux
import { useSelector, useDispatch } from "react-redux";
import {
  UserData,
  needUpdateUserType,
  setUserType as setUserTypeData,
  setNeedUpdateUserType as setNeedUpdateUserTypeData,
  IUserData,
} from "@src/@slice/userSlice";
import { callCount as callCountData } from "@src/@slice/globalSlice";
// types
import { IMe } from "@src/api/common";
// api 
import { getHomeMe } from "@src/api/common";
// utils
import {
  SOCIAL_LOGIN_MAP,
  encryptAES,
  ENCRYPTION_KEY,
} from "..";
// hooks
import { useUserActions } from "./useUserActions";
import { getOs, waitFor } from "@src/shared/src";
import { NavigateFunction } from "react-router-dom";

// constants
export const APP_HOME_PATH_KEY = "/home";
export const APP_START_PATH_KEY = "/sign-in/social";
export const SIGN_IN_PATH_KEY = "/sign-in/";
export const SIGN_UP_PATH_KEY = "/sign-up/";
export const SOCIAL_PATH_KEY = "/social/";


const useInitUser = ({
  userInfo,
  setUserInfo,
  initialData,
}: {
  userInfo: IMe;
  setUserInfo: (data: IMe) => void;
  initialData: IMe;
}) => {
  // redux
  const dispatch = useDispatch();
  const userData = useSelector(UserData);
  const callCount = useSelector(callCountData);
  const needLoginStatusUpdate = useSelector(needUpdateUserType);

  // call Count ref
  const callCountRef = useRef(null);

  // hooks
  const { handleConditionalLogout, getUserData, resetUserData } = useUserActions();

  // 있으면 처리에서 제외할 조건 params
  const pathname = window.location.pathname;
  const isAppStartPath = pathname === APP_START_PATH_KEY;
  const isInSignInPath = pathname?.includes(SIGN_IN_PATH_KEY);
  const isInSignUpPath = pathname?.includes(SIGN_UP_PATH_KEY);
  const isInSocialPath = pathname?.includes(SOCIAL_PATH_KEY);
  const isHomePath = pathname?.includes(APP_HOME_PATH_KEY);
  const queryParams = new URLSearchParams(window.location.search);
  const hasLoginParams = queryParams.get("skip");
  const validParamsToSchemeLogin = !hasLoginParams;

  // get user data - userAgent 기반 호출
  const getUserInfo = async (showAlert?: boolean) => {
    return getHomeMe()
      .then((res) => {
        localStorage.removeItem("tempCheckAccessToken");
        setUserInfo({ ...res.data.item, updated: true, loggedin: true });
        socialLoginSave(res.data.item);
        return true;
      })
      .catch(async (err) => {
        // userAgent에 userUuid가 없음 (처리 미완료 상태) - 홈에서만 처리하도록 설정 (1회만)
        if (!isHomePath) {
          setUserInfo({ ...initialData, updated: true, loggedin: false });
          return null;
        }

        const accessToken = localStorage.getItem("accessToken");
        const tempCheckAccessToken = localStorage.getItem("tempCheckAccessToken");

        if (tempCheckAccessToken) {
          resetUserData();
          setUserInfo({ ...initialData, updated: true, loggedin: false });
          localStorage.removeItem("tempCheckAccessToken");
          return null;
        }

        if (accessToken && accessToken !== "undefined" && !document.hidden) {
          const userUuid = getUserData(accessToken)?.customerUuid;
          if (userUuid) {
            localStorage.setItem("tempCheckAccessToken", "true");
            window.location.href = `h2mwbell://home?userUuid=${userUuid}`;
            return null;
          }
        }

        // fallback 처리
        setUserInfo({ ...initialData, updated: true, loggedin: false });
        return null;
      })
  };

  // 소셜 로그인 쿠키 저장(마지막 로그인 계정 노출용)
  const socialLoginSave = (data: IMe) => {
    if (!data?.me?.loginType) return;
    const isSocial = data.me.loginType === "socialAccount";
    const brand = data.me.socialLoginBrandName;
    if (isSocial && brand) {
      const socialIndex = JSON.parse(SOCIAL_LOGIN_MAP)[brand];
      const data = {
        payload: socialIndex,
        expires: new Date(Date.now() + 60 * 60 * 24 * 30 * 1000), // 30일
      };
      const encrypted = encryptAES(JSON.stringify(data), ENCRYPTION_KEY);
      localStorage.setItem("h2mwbell:last", encrypted);
    } else {
      localStorage.removeItem("h2mwbell:last");
    }
  };

  // 유저 상태 변경 핸들러
  const userTypeSettingHandler = async (needUserInfoUpdate?: boolean) => {
    // accessToken이 없으면 게스트 유저
    let accessToken = localStorage.getItem("accessToken");
    let refreshToken = localStorage.getItem("refreshToken");
    if (accessToken === 'undefined' || refreshToken === 'undefined') { 
      resetUserData();
      accessToken = null;
      refreshToken = null;
      return;
    }
    const isGuest = !accessToken;

    // home.me API 호출 (userInfo가 없을 경우)
    const hasUserInfo = !!userInfo?.me?.loginId;

    // userInfo, accessToken 둘 다 있어야 완료된 유저
    const isComplete = accessToken && hasUserInfo;

    // accessToken은 있지만 userInfo가 없을 경우 미완료 유저로 처리하여 API 호출 요청
    const isIncomplete = accessToken && !hasUserInfo;

    // accessToken 있으면 userData 가져오기
    if (accessToken) {
      getUserData(accessToken);
    }

    // 로그인 상태 업데이트 필요 여부
    if (
      isAppStartPath ||
      isInSignInPath ||
      isInSignUpPath ||
      isInSocialPath ||
      !validParamsToSchemeLogin
    )
      return;

    if (isIncomplete || (needUserInfoUpdate && !isGuest)) {
      // 미완료 유저거나 유저 정보 업데이트 필요
      const result = await getUserInfo(needLoginStatusUpdate);
      if (result) {
        if (isIncomplete) {
          dispatch(setUserTypeData({ status: "COMPLETE_AUTH_USER" }));
        }
      } else {
        dispatch(setUserTypeData({ status: "GUEST_USER" }));
      }
    } else if (isGuest) {
      // 게스트 유저
      dispatch(setUserTypeData({ status: "GUEST_USER" }));
      setUserInfo({ ...initialData, updated: true });
    } else if (isComplete) {
      // 완료된 유저
      // document.cookie = `h2mCookieAccessToken=${accessToken}; path=/; domain=.h2m.io; Secure; SameSite=None`;
      // document.cookie = `h2mCookieRefreshToken=${localStorage.getItem("refreshToken")}; path=/; domain=.h2m.io; Secure; SameSite=None`;
      dispatch(setUserTypeData({ status: "COMPLETE_AUTH_USER" }));
    }

    // 유저 상태 업데이트 필요 여부
    dispatch(setNeedUpdateUserTypeData({ status: false }));
  };

  // 로컬스토리지 변경 감지
  const storageChangeHandler = (e: StorageEvent) => {
    if (e.key === "accessToken" && !isAppStartPath && e.newValue !== e.oldValue) {
      userTypeSettingHandler();
    }
  };

  // 유저 상태에 따른 처리
  useEffect(() => {
    if (needLoginStatusUpdate || callCountRef.current !== callCount) {
      userTypeSettingHandler(true);
    }
    callCountRef.current = callCount;
  }, [needLoginStatusUpdate, callCount]);

  useEffect(() => {
    // native 연결용
    window.logoutAction = handleConditionalLogout;
    window.isNotificationEnabledResult = (result: string) => {
      const event = new CustomEvent("isNotificationEnabledResult", { detail: result });
      window.dispatchEvent(event);
    };
    window.checkAppVersionResult = (result: string) => {
      const event = new CustomEvent("checkAppVersionResult", { detail: result });
      window.dispatchEvent(event);
    };
    // 스토리지 변경 감지
    window.addEventListener("storage", storageChangeHandler);
    return () => {
      window.removeEventListener("storage", storageChangeHandler);
    };
  }, []);
};

export default useInitUser;

export const CHECK_SIGN_UP_COMPLETE_DATE = new Date("2025-04-16 19:00:00");

export const handleSchemeRedirect = async ({
  userData,
  navigate = null,
}: {
  userData: IUserData;
  navigate: NavigateFunction;
  }) => {
  if (getOs() === "ios") {
    await waitFor(600); // 일부러 지연
  } else {
    await waitFor(300);
  }
  let check: boolean | null = null;
  if (document.visibilityState === "hidden") return;

  // acessToken 및 refreshToken 체크
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");
  if (!accessToken || !refreshToken) {
    if (window.location.pathname !== APP_START_PATH_KEY) {
      // 엑세스 토큰 없으면 로그인 페이지로 이동
      window.location.href = `h2mwbell://login`;
    }
    return;
  }

  if (!userData?.customerUuid) return;
  const customerRegTsp = userData?.customerRegTsp;
  if (customerRegTsp) {
    const hasdWeddingTsp = !!userData?.weddingTsp;
    const regTsp = new Date(customerRegTsp);
    const isAfterDate = new Date(regTsp) > new Date(CHECK_SIGN_UP_COMPLETE_DATE);
    check = isAfterDate && !hasdWeddingTsp;
  }

  if (check === null) return;

  // 이동 로직
  if (check) {
    if (navigate) {
      navigate(`/sign-up/3`, { replace: true });
    }
  } else {
    window.location.href = `h2mwbell://home?userUuid=${userData.customerUuid}`;
  }
};
